import {
  MOBILE_HOME_FEED_MEDIA_ASPECT_RATIOS,
  type MobileHomeFeedMediaAspectRatio,
} from './cardDisplayModel';
import { type MobileHomeFeedVideoV3 } from './homeFeedHeroMedia';

export interface FeedVideoDimensions {
  width: number;
  height: number;
}

// Encoded feed assets use either tall 9:16 video or 4:5 gif-style loops.
const FEED_VIDEO_GIF_ASPECT_RATIO = 4 / 5;
const FEED_VIDEO_ASPECT_RATIO_THRESHOLD =
  (9 / 16 + FEED_VIDEO_GIF_ASPECT_RATIO) / 2;

const cloudflareVideoDimensionsCache = new Map<
  string,
  FeedVideoDimensions | null
>();

export function classifyFeedVideoAspectRatio({
  width,
  height,
}: FeedVideoDimensions): MobileHomeFeedMediaAspectRatio {
  if (width <= 0 || height <= 0) {
    return MOBILE_HOME_FEED_MEDIA_ASPECT_RATIOS.portrait9x14;
  }

  const widthToHeightRatio = width / height;

  return widthToHeightRatio >= FEED_VIDEO_ASPECT_RATIO_THRESHOLD
    ? MOBILE_HOME_FEED_MEDIA_ASPECT_RATIOS.portrait4x5
    : MOBILE_HOME_FEED_MEDIA_ASPECT_RATIOS.portrait9x14;
}

export function getCloudflareStreamMetadataUrl(playbackId: string): string {
  return `https://customer-upzz6isqwwa8euf7.cloudflarestream.com/${playbackId}/metadata/playerEnhancementInfo.json`;
}

export async function fetchCloudflareVideoDimensions(
  playbackId: string
): Promise<FeedVideoDimensions | null> {
  if (cloudflareVideoDimensionsCache.has(playbackId)) {
    return cloudflareVideoDimensionsCache.get(playbackId) ?? null;
  }

  try {
    const response = await fetch(getCloudflareStreamMetadataUrl(playbackId));

    if (!response.ok) {
      cloudflareVideoDimensionsCache.set(playbackId, null);
      return null;
    }

    const payload = (await response.json()) as {
      dimensions?: { width?: number; height?: number };
    };
    const width = payload.dimensions?.width;
    const height = payload.dimensions?.height;

    if (typeof width !== 'number' || typeof height !== 'number') {
      cloudflareVideoDimensionsCache.set(playbackId, null);
      return null;
    }

    const dimensions = { width, height };
    cloudflareVideoDimensionsCache.set(playbackId, dimensions);
    return dimensions;
  } catch {
    cloudflareVideoDimensionsCache.set(playbackId, null);
    return null;
  }
}

export async function prefetchCloudflareVideoDimensions(
  playbackIds: readonly string[]
): Promise<Map<string, FeedVideoDimensions>> {
  const uniquePlaybackIds = [...new Set(playbackIds)];

  await Promise.all(
    uniquePlaybackIds.map((playbackId) =>
      fetchCloudflareVideoDimensions(playbackId)
    )
  );

  const dimensionsByPlaybackId = new Map<string, FeedVideoDimensions>();

  for (const playbackId of uniquePlaybackIds) {
    const dimensions = cloudflareVideoDimensionsCache.get(playbackId);
    if (dimensions !== undefined && dimensions !== null) {
      dimensionsByPlaybackId.set(playbackId, dimensions);
    }
  }

  return dimensionsByPlaybackId;
}

export function collectFeedVideoPlaybackIds({
  pairings,
  products,
}: {
  pairings: ReadonlyArray<{ video?: MobileHomeFeedVideoV3 | null }>;
  products: ReadonlyArray<{ video?: MobileHomeFeedVideoV3 | null }>;
}): string[] {
  const playbackIds = new Set<string>();

  for (const item of [...pairings, ...products]) {
    const playbackId = item.video?.video_playback_id;
    if (playbackId !== undefined && playbackId !== '') {
      playbackIds.add(playbackId);
    }
  }

  return [...playbackIds];
}

export function clearCloudflareVideoDimensionsCacheForTests(): void {
  cloudflareVideoDimensionsCache.clear();
}
